import style from "./search-sensors.module.css";

import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { inject, observer } from "mobx-react";
import EditSensor from "./edit-sensor/edit-sensor";
import Web from "../web";
import {
    EmptySensorData,
    SensorDataStore,
    SENSORS_PER_PAGE,
} from "../store/store";
import { AuthStore, SearchStore, TransitionStore } from "../../../store";
import { SensorStatus } from "./cells";
import { IDeleteSensorData, SensorData } from "../store/types";
import { CreateConfirmationDialog } from "../../../utils/ConfirmationDialog";
import webResource from "../web";
import { GlobalDialogData } from "../../../store/transition-store";
import { NoConnectionModal } from "../../../utils/NoConnectionDialog";
import { NotificationDialog } from "../../../utils/NotificationDialog";
import {
    SortHeader,
    SORT_DIRECTION,
} from "../../../components/table/headers/SortHeader";
import {
    ActionsCell,
    ButtonAvailability,
    ButtonVisibility,
    BUTTON_TYPE,
} from "../../../components/table/cells/ActionsCell";
import {
    BaseTable,
    ColumnData,
    ExpandedState,
} from "../../../components/table/table";
import { TextCell } from "../../../components/table/cells/TextCell";
import { LocationCell } from "../../../components/table/cells/LocationCell";
import { SensorDataConverter } from "../store/convertor";
import { SensorDataIn, SensorListCallIn } from "../store/typesIn";
import IconTooltipCell from "./cells/IconTooltipCell";
import {
    BasicHeader,
    ColumnAlign,
} from "../../../components/table/headers/BasicHeader";
import { runInAction } from "mobx";
import { scrollIntoViewAndAlign } from "../../../utils/scroll";
import LocationSelectorStore from "../../../store/location-selector-store";
import UserSettingsStore, { SortSettings } from "../../../store/user-settings";
import { useNavigate, useSearchParams } from "react-router-dom";

interface IManageSensorsGridProps {
    authStore?: AuthStore;
    sensorDataStore?: SensorDataStore;
    transitionStore?: TransitionStore;
    searchStore?: SearchStore;
    locationSelectorStore?: LocationSelectorStore;
    userSettingsStore?: UserSettingsStore;
}

function ManageSensorsGrid(props: IManageSensorsGridProps) {
    const { t } = useTranslation(["sensor-grid"]);
    const sensorDataStore = props.sensorDataStore!;
    const transitionStore = props.transitionStore!;
    const locationSelectorStore = props.locationSelectorStore!;
    const userSettingsStore = props.userSettingsStore!;
    const authStore = props.authStore!;
    const searchStore = props.searchStore!;
    let navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const firstUpdate = useRef(true);
    const { requestIds, initialized } = locationSelectorStore;
    const { isLoading } = transitionStore;

    const {
        errors,
        selectedSensor,
        oldSensorData,
        validated,
        searchValue,
        filteringLocationId,
        newRequestPage,
    } = sensorDataStore;

    const sortOptions = userSettingsStore.getUserSetting(
        SortSettings.MANAGE_SENSORS
    );

    useEffect(() => {
        return () => {
            sensorDataStore.setFetchApiActiveId(null);
            sensorDataStore.setHiglight(null);
        };
    }, []);

    useEffect(() => {
        if (initialized) {
            const pageNumberParam = searchParams.get("page");
            let pageNum = 1;
            if (pageNumberParam) {
                pageNum = Number(pageNumberParam);
            }

            readAllSensors(pageNum);
        }
    }, [initialized, newRequestPage]);

    useLayoutEffect(() => {
        if (initialized) {
            if (firstUpdate.current) {
                firstUpdate.current = false;
                return;
            }
            readAllSensors(1);
        }
    }, [requestIds, searchValue, filteringLocationId]);

    const readAllSensors = (pageNumber: number) => {
        const sortOptions = userSettingsStore.getUserSetting(
            SortSettings.MANAGE_SENSORS
        );

        sensorDataStore.setIsDataLoading(true);

        const params: any = {
            Pagination: {
                CurrentPage: pageNumber,
                PerPage: SENSORS_PER_PAGE,
            },
            SortOrder: sortOptions.desc,
            SortBy: sortOptions.id,
            LocationId: filteringLocationId,
            FilteredLocationIds: locationSelectorStore.requestIds,

            SearchTerm: searchValue,
        };

        const noSensors: SensorData[] = [];

        const apiId = sensorDataStore.getNewApiId();
        sensorDataStore.setFetchApiActiveId(apiId);

        webResource
            .readAll(params)
            .then((ajaxResponse) => {
                if (apiId !== sensorDataStore.fetchApiActiveId) return;
                const response: SensorListCallIn = ajaxResponse.data!;

                const sensorList = response.data.map((sensor: SensorDataIn) => {
                    return SensorDataConverter.toStore(sensor);
                });

                sensorDataStore.setSensors(sensorList);

                sensorDataStore.setSensorTypes(response.sensorTypes);
                sensorDataStore.setIsDataLoading(false);

                let pageNumber = response.currentPage;
                let pageCount = response.noOfPages;
                let totalItemCount = response.count;

                if (pageNumber > pageCount) pageNumber = 1;

                navigate(`/manage-sensors?page=${pageNumber}`);

                sensorDataStore.setPageCount(pageCount);
                sensorDataStore.setTotalItemCount(totalItemCount);
                sensorDataStore.setPageNumber(pageNumber);

                if (searchStore.sensorSearch) {
                    const filteredSensors = sensorDataStore.sensors.filter(
                        (value) => {
                            if (!value.sensorId) return false;
                            return (
                                value.sensorId.toLowerCase() ===
                                searchStore.sensorSearch.toLowerCase()
                            );
                        }
                    );

                    if (filteredSensors && filteredSensors.length > 0) {
                        sensorDataStore.setHiglight(filteredSensors[0]);
                    }
                }
            })
            .then(() => {
                scrollHighlightIntoView();
            })
            .catch((ajaxError) => {
                const dialog: GlobalDialogData = NoConnectionModal(t);

                sensorDataStore.setSensors(noSensors);
                transitionStore.showGlobalDialog(dialog);
                sensorDataStore.setIsDataLoading(false);
            });
    };

    const scrollHighlightIntoView = () => {
        const selectedElement = document.querySelector("#selected-sensor");
        if (selectedElement) {
            scrollIntoViewAndAlign(selectedElement);
        }
    };

    const getSortDirection = (accessor: string) => {
        if (accessor !== sortOptions.id) return SORT_DIRECTION.NONE;

        if (sortOptions.desc) return SORT_DIRECTION.DESCENDING;
        else return SORT_DIRECTION.ASCENDING;
    };

    const actionButtonsAvailability = (
        index: number,
        selectedIndex: number
    ): ButtonAvailability => {
        let editAvailability: boolean = false;
        let deleteAvailability: boolean = false;

        if (selectedIndex === ExpandedState.COLLAPSED && !isLoading) {
            editAvailability = true;
            deleteAvailability = true;
        }

        if (!authStore.isAdmin()) {
            deleteAvailability = false;
        }

        return {
            edit: editAvailability,
            delete: deleteAvailability,
        };
    };

    const saveDisabled = () => {
        let saveDisabled = false;

        if (sensorDataStore.isSaveLoading) return true;

        if (JSON.stringify(selectedSensor) === JSON.stringify(oldSensorData)) {
            saveDisabled = true;
        }

        if (!validated) {
            saveDisabled = true;
        }

        if (errors && Object.keys(errors).length !== 0) {
            saveDisabled = true;
        }

        return saveDisabled;
    };

    const actionButtonsVisibility = (
        index: number,
        selectedIndex: number
    ): ButtonVisibility => {
        let editVisibility: boolean = false;
        let deleteVisibility: boolean = false;

        if (index !== selectedIndex) {
            editVisibility = true;
            deleteVisibility = true;
        }

        if (!authStore.isAdmin()) {
            deleteVisibility = false;
        }

        return {
            edit: editVisibility,
            delete: deleteVisibility,
        };
    };

    const save = () => {
        const { selectedSensor, selectedSensorHasChanges } = sensorDataStore;

        if (!selectedSensorHasChanges) return;

        if (selectedSensor.locationId === 0) {
            sensorDataStore.clearSelectedSensorLocationId();
        }

        sensorDataStore.setIsSaveLoading(true);

        const sensorData = SensorDataConverter.toOut(selectedSensor);

        Web.update(sensorData).then(
            () => {
                sensorDataStore.setSelectedSensorHasChanges(false);
                sensorDataStore.setIsNewSensor(false);
                clearSelectedSensor();

                const pageNumberParam = searchParams.get("page");
                let pageNum = 1;
                if (pageNumberParam) {
                    pageNum = Number(pageNumberParam);
                }

                readAllSensors(pageNum);
                sensorDataStore.setIsSaveLoading(false);
            },
            (err: any) => {
                sensorDataStore.setIsSaveLoading(false);
                err.message = t("Could not save or update sensor");
                resolveAjaxError(err.data);
                runInAction(() => {
                    sensorDataStore.selectedSensor.isNewSensor = true;
                });
            }
        );
    };

    const clearSelectedSensor = () => {
        sensorDataStore.setSelectedSensor(EmptySensorData);
        sensorDataStore.closeExpanded();
        sensorDataStore.bringHighlightedSensorIntoView = true;
    };

    const resolveAjaxError = (error: any) => {
        const manageStatusErrors = error.ManageSensor;

        if (
            manageStatusErrors === undefined ||
            manageStatusErrors.length === 0
        ) {
            reportAjaxError(error.message);
            return;
        }

        const errorData = manageStatusErrors[0];
        reportAjaxError(errorData);
    };
    const reportAjaxError = (errorMessage: string) => {
        const dialog: GlobalDialogData = NotificationDialog(
            t("ErrorTitleMessage"),
            t(errorMessage),
            t("Ok")
        );

        transitionStore?.showGlobalDialog(dialog);
    };

    const historyAccessor = "history";
    const storageAccessor = "storage";
    const sensorIdAccesor = "sensorId";
    const sensorTypeAccessor = "sensorType";
    const sensorLocationAccessor = "locationName";
    const sensorStatusAccessor = "status";

    const columnData: ColumnData[] = [
        {
            header: t("History"),
            width: "91px",
            HeaderComponent: (
                <BasicHeader text={t("History")} align={ColumnAlign.Center} />
            ),
            accesor: historyAccessor,
            cellComponent: (value) => {
                return <IconTooltipCell sensorId={value.sensorId} />;
            },
        },
        {
            header: t("OnOff"),
            accesor: storageAccessor,
            width: "8%",
            HeaderComponent: (
                <SortHeader
                    text={t("OnOff")}
                    selected={sortOptions.id === storageAccessor}
                    sortDirection={getSortDirection(storageAccessor)}
                    locked={sensorDataStore.hasSelectedSensor}
                    onClick={() => updateSorting(storageAccessor)}
                />
            ),
            cellComponent: (value: SensorData) => {
                if (value.storage) return <TextCell text={t("Storage")} />;
                else return <TextCell text={t("On")} />;
            },
        },
        {
            header: t("SensorId"),
            accesor: sensorIdAccesor,
            width: "12%",
            HeaderComponent: (
                <SortHeader
                    text={t("SensorId")}
                    selected={sortOptions.id === sensorIdAccesor}
                    sortDirection={getSortDirection(sensorIdAccesor)}
                    locked={sensorDataStore.hasSelectedSensor}
                    onClick={() => updateSorting(sensorIdAccesor)}
                />
            ),
            cellComponent: (value, index) => (
                <TextCell text={value.sensorId!.toUpperCase()} />
            ),
        },
        {
            header: t("SensorType"),
            accesor: sensorTypeAccessor,
            width: "12%",
            HeaderComponent: (
                <SortHeader
                    text={t("SensorType")}
                    selected={sortOptions.id === sensorTypeAccessor}
                    sortDirection={getSortDirection(sensorTypeAccessor)}
                    locked={sensorDataStore.hasSelectedSensor}
                    onClick={() => updateSorting(sensorTypeAccessor)}
                />
            ),
            cellComponent: (value: SensorData) => (
                <TextCell text={t(value.sensorTypeName)} />
            ),
        },
        {
            header: t("Room"),
            width: "24%",
            HeaderComponent: (
                <SortHeader
                    text={t("Room")}
                    selected={sortOptions.id === sensorLocationAccessor}
                    sortDirection={getSortDirection(sensorLocationAccessor)}
                    locked={sensorDataStore.hasSelectedSensor}
                    onClick={() => updateSorting(sensorLocationAccessor)}
                />
            ),
            cellComponent: LocationCell,
        },
        {
            header: t("Status"),
            accesor: sensorStatusAccessor,
            HeaderComponent: (
                <SortHeader
                    text={t("Status")}
                    selected={sortOptions.id === sensorStatusAccessor}
                    sortDirection={getSortDirection(sensorStatusAccessor)}
                    locked={sensorDataStore.hasSelectedSensor}
                    onClick={() => updateSorting(sensorStatusAccessor)}
                />
            ),
            cellComponent: (value: SensorData) => (
                <SensorStatus
                    statusId={value.status!}
                    inactivityPeriod={value.inactivityPeriod}
                />
            ),
        },
        {
            header: t("Actions"),
            width: "140px",
            HeaderComponent: (
                <BasicHeader text={t("Actions")} align={ColumnAlign.Center} />
            ),
            cellComponent: (value: SensorData, index: number) => (
                <ActionsCell
                    buttonVisibility={actionButtonsVisibility(
                        index,
                        sensorDataStore.expandedSensorIndex
                    )}
                    buttonAvailability={actionButtonsAvailability(
                        index,
                        sensorDataStore.expandedSensorIndex
                    )}
                    onButtonClick={(type) =>
                        onActionButtonClicked(value, index, type)
                    }
                />
            ),
        },
    ];

    const onActionButtonClicked = (
        sensorData: SensorData,
        index: number,
        buttonType: BUTTON_TYPE
    ) => {
        switch (buttonType) {
            case BUTTON_TYPE.EDIT: {
                sensorDataStore.bringHighlightedSensorIntoView = false;

                const location = locationSelectorStore.selectedRoomList.find(
                    (locationElement) => {
                        return (
                            Number(locationElement.id) ===
                            Number(sensorData.locationId)
                        );
                    }
                );

                let hasTelecareCapability = false;

                if (location) {
                    hasTelecareCapability = location?.hasTelecareCapability!;
                }

                const data: SensorData = {
                    id: sensorData.id,
                    sensorId: sensorData.sensorId,
                    sensorType: sensorData.sensorType,
                    sensorTypeName: sensorData.sensorTypeName,
                    storage: sensorData.storage,
                    status: sensorData.status,
                    inactivityPeriod: sensorData.inactivityPeriod,
                    availableSensorTypes: sensorData.availableSensorTypes,

                    locationId: sensorData.locationId,
                    locationName: sensorData.locationName,
                    areaName: sensorData.areaName,
                    isHeartbeatMonitored: sensorData.isHeartbeatMonitored,
                    heartbeatMonitoringIntervalTimeInMinutes:
                        sensorData.heartbeatMonitoringIntervalTimeInMinutes,
                    hasTelecareCapability: hasTelecareCapability,
                };

                sensorDataStore.setSelectedSensor(data);
                sensorDataStore.setSelectedSensorHasChanges(false);
                sensorDataStore.setExpandedSensorIndex(index);
                break;
            }

            case BUTTON_TYPE.DELETE: {
                onDeleteSensor(sensorData);
                break;
            }
        }
    };

    const updateSorting = (name: string) => {
        if (sortOptions.id === name) {
            userSettingsStore.setSortSetting(SortSettings.MANAGE_SENSORS, {
                id: name,
                desc: !sortOptions.desc,
            });
        } else {
            userSettingsStore.setSortSetting(SortSettings.MANAGE_SENSORS, {
                id: name,
                desc: false,
            });
        }
        const pageNumberParam = searchParams.get("page");
        let pageNum = 1;
        if (pageNumberParam) {
            pageNum = Number(pageNumberParam);
        }

        readAllSensors(pageNum);
    };

    const onDeleteSensor = (sensor: SensorData) => {
        const title = t("Remove sensor");
        const content = t("DeleteSensorDialogQuestion", {
            sensorId: sensor.sensorId,
        });

        const data = CreateConfirmationDialog(
            title,
            content,
            t("Remove"),
            t("Cancel"),
            false,
            () => {
                const data: IDeleteSensorData = {
                    sensorId: sensor.sensorId!,
                    locationId: sensor.locationId!,
                };
                sensorDataStore.setIsDeleteLoading(true);
                Web.remove(data).then(() => {
                    const pageNumberParam = searchParams.get("page");
                    let pageNum = 1;
                    if (pageNumberParam) {
                        pageNum = Number(pageNumberParam);
                    }

                    readAllSensors(pageNum);
                    sensorDataStore.setIsDeleteLoading(false);
                });
            }
        );

        transitionStore?.showGlobalDialog(data);
    };

    const highlightedItem = sensorDataStore.highlightSensor;

    return (
        <BaseTable
            minWidth={1286}
            expandedRows={sensorDataStore.expandedSensorIndex}
            data={sensorDataStore.sensors.slice()}
            columns={columnData}
            customRowId={(_index, rowData: SensorData) => {
                if (rowData.sensorId === highlightedItem?.sensorId)
                    return "selected-sensor";
                return "";
            }}
            customRowClass={(_index, rowData: SensorData) => {
                if (rowData.sensorId === highlightedItem?.sensorId)
                    return style["selected-sensor"];
                return "";
            }}
            expandedRowComponent={
                <EditSensor
                    onSaveClick={() => {
                        save();
                    }}
                    saveLocked={saveDisabled()}
                    onCancelClick={() => {
                        sensorDataStore.closeExpanded();
                    }}
                />
            }
        />
    );
}

export default inject(
    "sensorDataStore",
    "transitionStore",
    "authStore",
    "searchStore",
    "locationSelectorStore",
    "userSettingsStore"
)(observer(ManageSensorsGrid));
